import { useEffect } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { DashboardProvider } from ':cloud/contexts/DashboardProvider';
import { useGetProject } from ':cloud/hooks/useGetProject';
import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AppRoute } from ':cloud/utils/routes';
import { ProjectDashboardGettingStarted } from ':cloud/widgets/dashboard/ProjectDashboardGettingStarted';
import { ProjectDashboardMonitoring } from ':cloud/widgets/dashboard/ProjectDashboardMonitoring';
import { Layout } from ':cloud/widgets/layout';
import { ProjectDashboardHeader } from ':cloud/widgets/projects/ProjectDashboardHeader';
import { LoadingSpinner } from ':cloud/widgets/sharedcomponents';

function ProjectDashboardContent() {
  return (
    <VStack testID="dashboard-content" spacingBottom={3}>
      <VStack spacing={7}>
        <ProjectDashboardHeader />
      </VStack>
      <ProjectDashboardGettingStarted />
      <ProjectDashboardMonitoring />
    </VStack>
  );
}

export function ProjectDashboard() {
  const history = useHistory();
  const { projectId } = useParams<{ projectId: string }>();
  const { activeOrg } = useGetUser();
  const { selectedProject, setSelectedProject } = useAppState();
  const { projects, isLoading: isProjectsLoading } = useGetProjects(activeOrg?.organizationId);
  const foundProject = useGetProject(projectId);

  useLogViewOnMount(EventName.dashboard_page_view);

  useEffect(() => {
    if (!projects?.length) return;
    // if projectId in route isnt the same as the selected project
    if (foundProject && !isProjectsLoading && selectedProject?.id !== projectId) {
      setSelectedProject(foundProject);
    }
    // if selected project can't be found (suggests incorrect projectId param in route)
    const fallbackProject = projects[0];
    if (!foundProject) {
      history.push(`${AppRoute.Projects.Home}/${fallbackProject.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedProject AND setSelectedProject can cause infinite re-renders
  }, [projectId, projects, history, foundProject, isProjectsLoading]);

  /** if no projects exist has to be checked first, order matters */
  if (projects.length === 0) {
    return <Redirect to={AppRoute.Projects.Home} />;
  }

  if (!selectedProject) {
    return <LoadingSpinner />;
  }

  return (
    <Layout>
      <Layout.MainContainer spacingHorizontal={0} fullWidth>
        <DashboardProvider>
          <ProjectDashboardContent />
        </DashboardProvider>
      </Layout.MainContainer>
    </Layout>
  );
}
